import CreateActions from '../create/CreateActions';
import { Box } from '@mui/system';
import { AvatarVideosSection } from './AvatarVideosSection';
import { BrollVideosSection } from './BrollVideosSection';
import { WhatsNewSection } from './whats-new';
import { Styles } from '@t2c/shared-fe';
import { AIWorkBench } from './ai-work-bench';
import { AiWorkBenchOutputEnum } from '../../../t2c-fe/utils';
import { useState, useEffect } from 'react';
import { BrollClipsSection } from '../broll/BrollClipsSection';
import { useGetAllAiMediaData } from '../../../t2c-fe/services';
import { UploadTypes } from '../../../t2c-fe/@types';
import { GeneratedImagesSection } from '../text-to-image/GeneratedImagesSection';
import { GeneratedMusicsSection } from '../ai-music/GeneratedMusicsSection';
import { CreatedAudiosSection } from '../text-to-speech/CreatedAudiosSection';
import { useGetAllCreatedAudios } from '../../../t2c-fe/hooks';
import { useGetAllAvatarCreatedVideos } from '../../../t2c-fe/services/creations/getAllAvatarCreatedVideos';
import { useAuth } from '../../../t2c-fe/hooks/useAuth';
import SecureSignInLoader from './SecureSignInLoader';

function DashboardPage() {
  const [outputType, setOutputType] = useState<AiWorkBenchOutputEnum>(AiWorkBenchOutputEnum.IMAGE);

  const { auth, userLoading } = useAuth();
  const [authInProgress, setAuthInProgress] = useState(true); // default true to avoid FOUC

  useEffect(() => {
    // Detect if an OAuth/PKCE flow is in progress (values set before redirect)
    let inProgress = false;
    try {
      const ss = typeof window !== 'undefined' ? window.sessionStorage : undefined;
      const ls = typeof window !== 'undefined' ? window.localStorage : undefined;
      inProgress = !!(
        (ss && (ss.getItem('oauth_state') || ss.getItem('pkce_code_verifier'))) ||
        (ls && (ls.getItem('oauth_state_bak') || ls.getItem('pkce_code_verifier_bak')))
      );
    } catch {}
    setAuthInProgress(inProgress);
  }, []);

  const showLoading = userLoading || (!auth.isAuthenticated && authInProgress);
  // Global GuestVerifyGate handles guest modal now

  const isImagePage = outputType === AiWorkBenchOutputEnum.IMAGE;
  const isBrollCLipPage = outputType === AiWorkBenchOutputEnum.BROLL;
  const isMusicPage = outputType === AiWorkBenchOutputEnum.MUSIC;
  const isTextPage = outputType === AiWorkBenchOutputEnum.TEXT;
  const isTakingFacePage = outputType === AiWorkBenchOutputEnum.TALKING_FACE;
  const isVoiceOverPage = outputType === AiWorkBenchOutputEnum.VOICEOVER;

  const { data, isLoading, error, refetch } = useGetAllAiMediaData({
    type: isImagePage ? UploadTypes.Image : isMusicPage ? UploadTypes.Audio : UploadTypes.Video,
    limit: 4,
    sortOrder: 'desc',
    sortBy: 'updatedAt',
  });

  const {
    data: createdTextToSpeechData,
    refetch: refetchTextToSpeechData,
    isLoading: isFetchingTextToSpeechData,
  } = useGetAllCreatedAudios({
    limit: 4,
    sortOrder: 'desc',
    sortBy: 'updatedAt',
  });

  const {
    data: talkingFacesData,
    isLoading: loading,
    refetch: refetcTalkingFaces,
  } = useGetAllAvatarCreatedVideos({
    limit: 4,
    sortOrder: 'desc',
    sortBy: 'updatedAt',
  });

  if (showLoading) {
    return <SecureSignInLoader />;
  }

  return (
    <Box data-tut='main-layout-page' id='main-layout-page'>
      <Box p={3} pt={0}>
        <AIWorkBench
          outputType={outputType}
          setOutputType={setOutputType}
          refetch={
            isVoiceOverPage
              ? refetchTextToSpeechData
              : isTakingFacePage
              ? refetcTalkingFaces
              : refetch
          }
        />
      </Box>
      <Box sx={{ background: (theme) => theme.palette.background.primary, p: 3, mb: 2 }}>
        {/* <WhatsNewSection children={<CreateActions />} /> */}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6, paddingX: 3 }}>
        {isBrollCLipPage && (
          <BrollClipsSection data={data} isLoading={isLoading} refetch={refetch} />
        )}

        {isImagePage && (
          <GeneratedImagesSection data={data} isLoading={isLoading} refetch={refetch} />
        )}

        {isMusicPage && (
          <GeneratedMusicsSection data={data} isLoading={isLoading} refetch={refetch} />
        )}

        {isTakingFacePage && (
          <AvatarVideosSection
            title="Recent Creations"
            loading={loading}
            refetchData={refetcTalkingFaces}
            videoData={talkingFacesData}
          />
        )}

        {isVoiceOverPage && (
          <CreatedAudiosSection
            isDataLoading={isFetchingTextToSpeechData}
            data={createdTextToSpeechData}
            refetchTextToSpeechData={refetchTextToSpeechData}
            allAudios={false}
          />
        )}
      </Box>
    </Box>
  );
}

const styles: Styles = {
  container: {
    padding: 2,
  },
};

export default DashboardPage;
